import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import MultiRfidSearchComponent from '../components/MultiRfidSearchComponent';
import { getCoral, getCoralByRfid, removeCoralRfids, updateCoral } from '../services/api';
import { CoralResponse, RfidRemovalResult } from '../types';

type SearchMode = 'rfid' | 'coralId';

const statusColour = (
  status: RfidRemovalResult['status']
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'REMOVED') return 'success';
  if (status === 'ALREADY_UNATTACHED') return 'warning';
  if (status === 'FAILED' || status === 'INVALID' || status === 'NOT_FOUND') return 'error';
  return 'default';
};

const RemoveRfidTags: React.FC = () => {
  const [searchMode, setSearchMode] = useState<SearchMode>('rfid');
  const [coralIdInput, setCoralIdInput] = useState('');
  const [staged, setStaged] = useState<CoralResponse[]>([]);
  const [reason, setReason] = useState('');
  const [searching, setSearching] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [results, setResults] = useState<RfidRemovalResult[]>([]);
  const [replacementTarget, setReplacementTarget] = useState<RfidRemovalResult | null>(null);
  const [replacementRfid, setReplacementRfid] = useState('');
  const [replacementError, setReplacementError] = useState<string | null>(null);
  const [replacementSaving, setReplacementSaving] = useState(false);
  const replacementInputRef = useRef<HTMLInputElement>(null);

  const stagedIds = useMemo(() => new Set(staged.map((coral) => coral.coralId)), [staged]);

  const addCoralsToStage = (corals: CoralResponse[]) => {
    const existing = new Set(stagedIds);
    const added: CoralResponse[] = [];
    let duplicateCount = 0;
    let untaggedCount = 0;

    corals.forEach((coral) => {
      if (!coral.rfid) {
        untaggedCount += 1;
      } else if (existing.has(coral.coralId)) {
        duplicateCount += 1;
      } else {
        existing.add(coral.coralId);
        added.push(coral);
      }
    });

    if (added.length) {
      setStaged((current) => [...current, ...added]);
    }

    const messages: string[] = [];
    if (added.length) messages.push(`${added.length} coral${added.length === 1 ? '' : 's'} staged`);
    if (duplicateCount) messages.push(`${duplicateCount} already staged`);
    if (untaggedCount) messages.push(`${untaggedCount} already untagged`);
    setSearchMessage(messages.join('; ') || null);
  };

  const handleRfidSearch = async (rfids: string[]) => {
    setSearching(true);
    setError(null);
    setSearchMessage(null);
    try {
      const lookups = await Promise.allSettled(rfids.map((rfid) => getCoralByRfid(rfid)));
      const found: CoralResponse[] = [];
      let notFound = 0;
      let failed = 0;
      lookups.forEach((lookup) => {
        if (lookup.status === 'fulfilled' && lookup.value) {
          found.push(lookup.value);
        } else if (lookup.status === 'fulfilled') {
          notFound += 1;
        } else {
          failed += 1;
        }
      });
      addCoralsToStage(found);
      if (notFound || failed) {
        setError(
          [
            notFound ? `${notFound} RFID${notFound === 1 ? '' : 's'} not found` : '',
            failed ? `${failed} lookup${failed === 1 ? '' : 's'} failed` : '',
          ]
            .filter(Boolean)
            .join('; ')
        );
      }
    } finally {
      setSearching(false);
    }
  };

  const handleCoralIdSearch = async () => {
    const coralId = coralIdInput.trim();
    if (!coralId) return;
    setSearching(true);
    setError(null);
    setSearchMessage(null);
    try {
      const coral = await getCoral(coralId);
      addCoralsToStage([coral]);
      setCoralIdInput('');
    } catch (lookupError: any) {
      setError(lookupError.message || 'Unable to find coral');
    } finally {
      setSearching(false);
    }
  };

  const handleRemoveBatch = async () => {
    setConfirmOpen(false);
    setRemoving(true);
    setError(null);
    try {
      const response = await removeCoralRfids(
        staged.map((coral) => ({ coralId: coral.coralId, rfid: coral.rfid || '' })),
        reason.trim()
      );
      setResults(response.items);
      const completedIds = new Set(
        response.items
          .filter((item) => item.status === 'REMOVED' || item.status === 'ALREADY_UNATTACHED')
          .map((item) => item.coralId)
      );
      setStaged((current) => current.filter((coral) => !completedIds.has(coral.coralId)));
      setReason('');
    } catch (removalError: any) {
      setError(removalError.message || 'Unable to remove RFID tags');
    } finally {
      setRemoving(false);
    }
  };

  const openReplacement = (result: RfidRemovalResult) => {
    setReplacementTarget(result);
    setReplacementRfid('');
    setReplacementError(null);
    setTimeout(() => replacementInputRef.current?.focus(), 0);
  };

  const handleAttachReplacement = async () => {
    if (!replacementTarget) return;
    const rfid = replacementRfid.trim().toUpperCase();
    if (!/^[A-Z0-9]{24}$/.test(rfid)) {
      setReplacementError('RFID must be exactly 24 alphanumeric characters');
      return;
    }
    setReplacementSaving(true);
    setReplacementError(null);
    try {
      const coral = await updateCoral(replacementTarget.coralId, { rfid });
      if (!coral) throw new Error('Invalid update response');
      setResults((current) =>
        current.map((item) =>
          item.coralId === replacementTarget.coralId
            ? { ...item, coral, message: `Removed; replacement ${rfid} attached` }
            : item
        )
      );
      setReplacementTarget(null);
    } catch (attachError: any) {
      setReplacementError(attachError.message || 'Unable to attach replacement RFID');
    } finally {
      setReplacementSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Remove RFID Tags
          </Typography>
          <Typography color="text.secondary">
            Find existing corals, stage the incorrect tags, and remove them without changing coral
            status, lineage, or other properties.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {searchMessage && (
          <Alert severity="info" onClose={() => setSearchMessage(null)}>
            {searchMessage}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <ToggleButtonGroup
              value={searchMode}
              exclusive
              onChange={(_, value: SearchMode | null) => value && setSearchMode(value)}
              size="small"
              aria-label="coral lookup mode"
            >
              <ToggleButton value="rfid">RFID scan</ToggleButton>
              <ToggleButton value="coralId">Coral ID</ToggleButton>
            </ToggleButtonGroup>

            {searchMode === 'rfid' ? (
              <MultiRfidSearchComponent onSearch={handleRfidSearch} isSearching={searching} />
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  value={coralIdInput}
                  onChange={(event) => setCoralIdInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleCoralIdSearch();
                    }
                  }}
                  label="Internal Coral ID"
                  placeholder="Enter CoralID"
                  fullWidth
                  size="small"
                  disabled={searching}
                />
                <Button
                  variant="contained"
                  onClick={handleCoralIdSearch}
                  disabled={!coralIdInput.trim() || searching}
                  startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
                >
                  Search
                </Button>
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">Removal list ({staged.length})</Typography>
              <Typography variant="body2" color="text.secondary">
                Review every coral before executing the batch removal.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Coral ID</TableCell>
                    <TableCell>Active RFID</TableCell>
                    <TableCell>Species</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Remove</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staged.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          Scan an RFID or search by CoralID to build the removal list.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    staged.map((coral) => (
                      <TableRow key={coral.coralId}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{coral.coralId}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{coral.rfid}</TableCell>
                        <TableCell>{coral.speciesCode}</TableCell>
                        <TableCell>
                          <Chip size="small" label={coral.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove from staged list">
                            <IconButton
                              aria-label={`Remove ${coral.coralId} from staged list`}
                              onClick={() =>
                                setStaged((current) =>
                                  current.filter((item) => item.coralId !== coral.coralId)
                                )
                              }
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TextField
              label="Reason for unattachment"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
              helperText={`${reason.length}/500 — saved in each coral's RFID history`}
              disabled={!staged.length || removing}
            />
            <Box>
              <Button
                variant="contained"
                color="warning"
                disabled={!staged.length || !reason.trim() || removing}
                onClick={() => setConfirmOpen(true)}
                startIcon={removing ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
              >
                {removing
                  ? 'Removing tags...'
                  : `Remove ${staged.length} RFID tag${staged.length === 1 ? '' : 's'}`}
              </Button>
            </Box>
          </Stack>
        </Paper>

        {results.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Latest batch results
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Coral ID</TableCell>
                    <TableCell>Removed RFID</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell align="right">Replacement</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result, index) => (
                    <TableRow key={`${result.coralId}-${result.rfid}-${index}`}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {result.coralId || '-'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{result.rfid || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={result.status.replace(/_/g, ' ')}
                          color={statusColour(result.status)}
                        />
                      </TableCell>
                      <TableCell>{result.message}</TableCell>
                      <TableCell align="right">
                        {result.status === 'REMOVED' && !result.coral?.rfid && (
                          <Button
                            size="small"
                            startIcon={<AddLinkIcon />}
                            onClick={() => openReplacement(result)}
                          >
                            Attach RFID
                          </Button>
                        )}
                        {result.coral?.rfid && (
                          <Chip size="small" color="success" label={result.coral.rfid} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm RFID removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove {staged.length} active RFID tag{staged.length === 1 ? '' : 's'}? Coral status,
            lineage, and other properties will remain unchanged. The RFID, date, reason, and
            operator will be saved in each coral&apos;s unattachment history.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleRemoveBatch}>
            Confirm removal
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(replacementTarget)}
        onClose={() => !replacementSaving && setReplacementTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Attach replacement RFID</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <DialogContentText>
              Scan the new tag for CoralID {replacementTarget?.coralId}. This updates the same coral
              record; its history and lineage remain unchanged.
            </DialogContentText>
            {replacementError && <Alert severity="error">{replacementError}</Alert>}
            <TextField
              inputRef={replacementInputRef}
              autoFocus
              label="New RFID"
              value={replacementRfid}
              onChange={(event) => setReplacementRfid(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAttachReplacement();
                }
              }}
              inputProps={{ maxLength: 24 }}
              helperText="Exactly 24 alphanumeric characters"
              disabled={replacementSaving}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplacementTarget(null)} disabled={replacementSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAttachReplacement}
            disabled={replacementSaving}
          >
            {replacementSaving ? 'Attaching...' : 'Attach RFID'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RemoveRfidTags;
